import {Page} from "../page";

export const ticketing: Page = {
    uri: 'learn/ticketing',
    icon: '🎟️',
    title: $localize`:@@page-ticketing-title:Ticketing`,
    seoTitle: $localize`:@@page-ticketing-seo-title:Ludoya Tickets — Stripe setup, ticket types, refunds and check-in`,
    summary: $localize`:@@page-ticketing-summary:Sell paid tickets for your events with Ludoya Tickets — connect Stripe, set up ticket types, and check attendees in with QR codes.`,
    markdownContent: [
        $localize`:@@page-ticketing-connecting-stripe-tier-2026-10-16:Ludoya Tickets lets [${'Business'} plan](/premium) groups sell paid admission to events entirely inside Ludoya — no external ticketing platform, and no redirect for the buyer. This page covers connecting Stripe, defining ticket types, tracking sales and refunds, and checking attendees in as an organizer, plus buying, holding, and using tickets as an attendee.

## Connecting Stripe (${'Business'} plan)

Before you can sell tickets, your group needs a connected Stripe account.

### Setting Up

![Connecting Stripe: buyers pay your Stripe account directly, with the 3% fee stated on the card](/assets/screenshots/ticketing-connect-stripe.webp)

1. Open your club's [console](/learn/club-console) and go to **Plan and payouts**
2. Click **Connect Stripe**
3. Complete Stripe's own onboarding (business details, banking information, identity verification)
4. Return to Ludoya — your status chip updates automatically

### Connection Status

- **Not connected** — no Stripe account linked yet
- **Onboarding started** — you began Stripe's setup but haven't finished it; **Continue setup** picks it up
- **Documents needed** — Stripe needs more verification before it can accept charges
- **Ready** — you can now offer Ludoya Tickets on your events

### How the Money Works

You are the merchant of record for every sale — the buyer pays your Stripe account directly, so tax/VAT, disputes, chargebacks, and payouts are all yours, managed on your own Stripe dashboard.

**The buyer pays exactly the ticket price you set.** Nothing is added at checkout. Ludoya's **3% fee** comes out of that amount before it reaches you, and Stripe's own processing fees are deducted as usual — so on a €10 ticket, €0.30 goes to Ludoya and the rest lands in your account minus Stripe's cut. If you want to cover those costs, build them into the price.`,

        $localize`:@@page-ticketing-setting-up-ticket-types:## Setting Up Ticket Types

Once your account is Ready, choose **Ludoya Tickets** as the event's Attendance Mode when creating or editing an [event](/learn/events) — it replaces the RSVP button with **Get Tickets** for attendees.

### Defining a Ticket Type

![Ticket type editor showing name, price, quantity, and visibility](/assets/screenshots/ticketing-types-editor.webp)

1. In the event editor, with **Ludoya Tickets** as the attendance mode, tap **Edit ticket types**
2. Click **Add ticket type**
3. Enter a **name** and **description**
4. Set a **price** and your event's **currency** — every ticket type on the same event shares one currency, since a purchase is a single payment
5. Set the **quantity available** (can't exceed your event's attendee limit) and a **max per person**
6. Choose **visibility**: Public, Group members only, or Access code (only people who enter the code can see and buy it)
7. Optionally set a **member price**: what the club's members pay when their [dues](/learn/membership-tiers) are in order. It can't be higher than the ticket's price
8. Save — repeat for as many ticket types as you need (e.g., "General", "VIP", "Kids")

### Editing Prices

Changing a ticket type's price doesn't affect tickets already sold — buyers keep the price they paid at checkout. Ludoya handles this automatically behind the scenes.

### Copies and Recurring Events

Ticket types carry over automatically when you copy an event or set up a recurring series, so you don't have to redefine them each time.

Across every event, **Plan and payouts** in the console adds up what the club has collected, with **Sales by event** and the tickets sold.`,

        $localize`:@@page-ticketing-selling-and-tracking-sales:## Selling and Tracking Sales

### The Sales Panel

![Sales panel showing sold/remaining counts, revenue, and order list](/assets/screenshots/ticketing-sales-panel.webp)

Organizers see a **Ticket sales** card on the event page, with:

- Sold, remaining, and revenue per ticket type
- The full **order list** — buyer, how many tickets, attendee names, and status
- Your connected account's **payout status**

### Issuing Refunds

1. Open **Ticket sales** on the event page and find the order or ticket you want to refund
2. Click **Refund** on a single ticket or on the whole order
3. Confirm — the exact refund amount is shown before you confirm
4. The ticket is marked **voided** once Stripe confirms the refund (usually within moments, not instantly)

Refunds happen entirely in Ludoya — you never need to open Stripe for a routine refund. Disputes and chargebacks, if they happen, are handled on your Stripe dashboard.

### Refunds and Fees

When you issue a refund, the buyer gets the ticket price back and **Ludoya's 3% service fee is returned to you** — on a partial refund, the matching share of the fee comes back. Only Stripe's own processing fees are not returned, per Stripe's standard policy.

Late payments against an expired checkout hold are refunded automatically and in full, with no action from you. See **The Checkout Hold** below for how that window works.

### Canceling a Ticketed Event

Canceling an event **does not automatically refund anyone** — the sales panel keeps working after cancelation precisely so you can refund buyers on your own terms. If you cancel an event that has sold tickets, open **Ticket sales** and refund the orders (individually or all of them) yourself. You also can't delete an event while it still has active or paid orders — refund every ticket first.`,

        $localize`:@@page-ticketing-checking-attendees-in:## Checking Attendees In

Ticketed events use the same QR scanner as regular [QR entry verification](/learn/event-operations), upgraded to work per ticket:

1. Open the event's **QR scanner** at the door
2. Scan the attendee's ticket QR code
3. Ludoya shows their name, ticket type, and how many of their order's tickets are already checked in (e.g., "2 of 3 checked in")
4. Tap **Check In** to confirm — the ticket and the attendee's Showed Up status are both marked

Voided or unpaid tickets are rejected with a clear reason on the scanner screen. Each ticket in a group checks in independently, so a group can arrive together or split up without any trouble.`,

        $localize`:@@page-ticketing-buying-tickets:## Buying Tickets

### Getting Tickets

![The Get Tickets sheet: three ticket types with quantity steppers, stock left and the total on the Continue button](/assets/screenshots/ticketing-buyer-sheet.webp)

1. Open the event page and tap **Get Tickets**
2. Choose a quantity for each ticket type you want (enter an **access code** first if the organizer gave you one for a hidden type)
3. Name each ticket — yourself, a friend's Ludoya account (search for them), or a guest name
4. No account? Enter your name and email for a quick signup before paying
5. Pay with a card right in the dialog — no redirect to another site
6. Your tickets appear in your wallet within a few seconds

### Your Ticket Wallet

All your tickets for an event — your own plus any you bought for friends — show up together as a stack of QR codes, each labeled with the attendee's name and ticket type. A ticket greys out once it's checked in or refunded, so you can see at a glance what's still valid.

### Tickets for Friends and Guests

If you buy a ticket for a friend who has a Ludoya account, it also appears on their own event page.

You can also buy for someone who isn't on Ludoya at all — just give their name. **Their ticket comes to you, not to them:** your receipt email contains a separate link for every ticket in the order, so forward the right one and they can open their QR code without an account or a login. That also means you keep the whole order in your own wallet, which is usually what you want when you're buying for a group.`,

        $localize`:@@page-ticketing-checkout-hold:### The Checkout Hold

Picking tickets reserves them for **30 minutes** while you pay. If you don't finish in time the seats go back on sale, so someone else can have them rather than the event sitting half-blocked by abandoned baskets.

If a payment somehow completes after that window has closed, Ludoya refunds it in full automatically — you're never charged for a seat you didn't get.

### Your Receipt

After paying, you get an email receipt with a summary of your order and a link to each individual ticket.`,

        $localize`:@@page-ticketing-related:## Related

- [Events](/learn/events) — attendance modes, RSVP, joining and hosting
- [Event Operations](/learn/event-operations) — QR entry verification, floor plans, custom forms
- [Premium](/premium) — ${'Business'} plan feature breakdown
- [Integrations](/learn/integrations) — connecting Stripe and other external services
- [Club Console](/learn/club-console) — Plan and payouts, where Stripe is connected
- [Guide: Stores & Venues](/learn/guide-stores-venues) — the complete workflow for venue operators`,
    ].join('\n\n---\n\n'),
};
